import { Outlet, useLocation, Link } from 'react-router-dom';
import { useNoindex } from '@/hooks/useNoindex';

const BARS = [43, 58, 50, 65, 60, 73, 68, 80, 75, 87, 83, 91, 96];

const AuthLayout: React.FC = () => {
  // Sign-in and sign-up flows are never indexed.
  useNoindex();

  const { pathname } = useLocation();
  const isLogin = pathname === '/login';

  return (
    <div
      className="min-h-screen grid grid-cols-1 lg:grid-cols-2 text-[#f0f0f3]"
      style={{ background: '#0b0b0e', fontFamily: "'Geist','Inter',system-ui,sans-serif" }}
    >
      {/* ── Left column: nav + form well + footer ── */}
      <div className="flex flex-col px-10 py-7">

        {/* Top nav */}
        <div className="flex items-center justify-between pb-8">
          <div className="flex items-center gap-2">
            {/* Decorative: the wordmark beside it already says DebtDrone. */}
            <img src="/debtdrone-logo.svg" alt="" width={28} height={28} className="h-[28px] w-[28px]" decoding="async" />
            <span className="text-[14px] font-semibold tracking-[-0.3px]">DebtDrone</span>
          </div>
          <div className="text-[11px] text-[rgba(240,240,243,.5)]">
            {isLogin ? (
              <>New here?<Link to="/signup" className="ml-[4px] text-[#f0f0f3] underline underline-offset-[3px] hover:opacity-80 transition-opacity">Create account</Link></>
            ) : (
              <>Already have an account?<Link to="/login" className="ml-[4px] text-[#f0f0f3] underline underline-offset-[3px] hover:opacity-80 transition-opacity">Sign in</Link></>
            )}
          </div>
        </div>

        {/* Form well — Outlet renders LoginForm or SignupForm here */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-[340px]">
            <Outlet />
          </div>
        </div>

        {/* Bottom footer */}
        <div className="flex items-center justify-between pt-6 mt-auto font-mono text-[11px] text-[rgba(240,240,243,.35)]">
          <span>© 2026 DebtDrone</span>
          <div className="flex gap-4">
            {['Status', 'Docs', 'Contact'].map(label => (
              <a key={label} href="#" className="transition-colors hover:text-[rgba(240,240,243,.7)]">{label}</a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right showcase panel ── */}
      <div className="relative hidden lg:flex flex-col overflow-hidden border-l border-[rgba(255,255,255,.08)] bg-[#111115]">

        {/* Grid texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Top lime glow */}
        <div
          className="absolute inset-x-0 top-0 h-px pointer-events-none"
          style={{ background: 'linear-gradient(90deg,transparent,rgba(181,232,74,.4),transparent)' }}
        />

        <div className="relative flex flex-1 flex-col p-[40px_44px]">

          {/* Live badge */}
          <div className="flex items-center gap-[6px] font-mono text-[10px] uppercase tracking-[.06em] text-[rgba(240,240,243,.4)]">
            <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[#3aaa70]" />
            {import.meta.env.VITE_WAITLIST_MODE === 'true' ? 'Closed Beta · Early Access' : 'Live · Shipping fast'}
          </div>

          <div className="flex-1" />

          {/* Scan card */}
          <div
            className="mb-8 rounded-[12px] border border-[rgba(255,255,255,.08)] p-[18px_20px] shadow-[inset_0_1px_0_rgba(255,255,255,.04)]"
            style={{ background: 'rgba(11,11,14,.6)', backdropFilter: 'blur(12px)' }}
          >
            {/* Card header */}
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-[7px]">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.4)" strokeWidth="2">
                  <line x1="6" y1="3" x2="6" y2="15" />
                  <circle cx="18" cy="6" r="3" />
                  <circle cx="6" cy="18" r="3" />
                  <path d="M18 9a9 9 0 0 1-9 9" />
                </svg>
                <span className="font-mono text-[11px] text-[rgba(240,240,243,.4)]">acme/payments-api</span>
                <span className="font-mono text-[10px] text-[rgba(240,240,243,.4)]">main</span>
              </div>
              <span className="grid h-[24px] w-[24px] place-items-center rounded-[5px] bg-[rgba(181,232,74,.15)] font-mono text-[12px] font-bold text-[#b5e84a]">A</span>
            </div>

            {/* Stats */}
            <div className="mb-4 grid grid-cols-3 gap-4">
              {[
                { label: 'Debt',     value: '12.4h', delta: '−1.8h' },
                { label: 'Issues',   value: '127',   delta: '−23'   },
                { label: 'Coverage', value: '84%',   delta: '+2.1'  },
              ].map(({ label, value, delta }) => (
                <div key={label}>
                  <div className="mb-1 font-mono text-[9px] uppercase tracking-[.08em] text-[rgba(240,240,243,.4)]">{label}</div>
                  <div className="font-mono text-[20px] font-semibold leading-none text-[#f0f0f3]">{value}</div>
                  <div className="mt-[2px] font-mono text-[10px] text-[#3aaa70]">{delta}</div>
                </div>
              ))}
            </div>

            {/* Bar chart */}
            <div className="flex h-[48px] items-end gap-[3px]">
              {BARS.map((h, i) => (
                <div
                  key={i}
                  className="flex-1 rounded-[2px]"
                  style={{ height: `${h}%`, background: 'linear-gradient(to top,rgba(181,232,74,.3),#b5e84a)' }}
                />
              ))}
            </div>
          </div>

          {/* Headline */}
          <div className="mb-6">
            <h2 className="text-[24px] font-semibold leading-[1.25] tracking-[-0.5px] text-[#f0f0f3]">
              Ship cleaner code,<br />
              <span className="text-[rgba(240,240,243,.4)]">measure it like revenue.</span>
            </h2>
            <p className="mt-[10px] max-w-[340px] text-[13px] leading-[1.65] text-[rgba(240,240,243,.4)]">
              DebtDrone turns every commit into a debt signal — graded, costed, and routed to the engineer who can fix it fastest.
            </p>
          </div>

          {/* Feature pills */}
          <div className="mb-7 grid grid-cols-3 gap-2">
            <div className="flex items-center gap-[7px] rounded-[8px] border border-[rgba(255,255,255,.08)] bg-[rgba(11,11,14,.4)] p-[8px_12px] font-mono text-[10px] text-[rgba(240,240,243,.4)]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#b5e84a" strokeWidth="2">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10" />
              </svg>
              12s scan p95
            </div>
            <div className="flex items-center gap-[7px] rounded-[8px] border border-[rgba(255,255,255,.08)] bg-[rgba(11,11,14,.4)] p-[8px_12px] font-mono text-[10px] text-[rgba(240,240,243,.4)]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#b5e84a" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              End-to-end encrypted
            </div>
            <div className="flex items-center gap-[7px] rounded-[8px] border border-[rgba(255,255,255,.08)] bg-[rgba(11,11,14,.4)] p-[8px_12px] font-mono text-[10px] text-[rgba(240,240,243,.4)]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#b5e84a" strokeWidth="2">
                <line x1="6" y1="3" x2="6" y2="15" />
                <circle cx="18" cy="6" r="3" />
                <circle cx="6" cy="18" r="3" />
                <path d="M18 9a9 9 0 0 1-9 9" />
              </svg>
              8 git hosts
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
